import type { DateValidationError } from '@mui/x-date-pickers';
import type { Control, FieldErrors, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import type { Renter } from '@/utils/types/renter.interface';

import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';

import { LeaseDatePicker } from './LeaseDatePicker';

type LeaseStartDatePickerProps = {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  setValue: UseFormSetValue<Renter>;
  trigger: UseFormTrigger<Renter>;
  endDateManuallyEdited: React.MutableRefObject<boolean>;
};

export const LeaseStartDatePicker = ({
  control,
  errors,
  setValue,
  trigger,
  endDateManuallyEdited,
}: LeaseStartDatePickerProps) => {
  const error = errors.leaseStart;

  const onChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue !== null && !newValue.isValid()) {
      return;
    }

    const formattedValue = newValue ? newValue.toDate() : null;

    setValue('leaseStart', formattedValue as Date, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    if (formattedValue && !endDateManuallyEdited.current) {
      const newEndDate = dayjs(formattedValue).add(12, 'month').subtract(1, 'day').toDate();
      setValue('leaseEnd', newEndDate, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    trigger('leaseEnd');
    trigger('leaseStart');
  };

  const onError = (_validationError: DateValidationError, value: dayjs.Dayjs | null) => {
    if (value?.isValid()) {
      setValue('leaseStart', value.toDate());
    }
  };

  return (
    <Controller
      name="leaseStart"
      control={control}
      render={({ field }) => {
        const pickerValue = field.value ? dayjs(field.value) : null;

        return (
          <LeaseDatePicker
            title="Lease start date"
            value={pickerValue}
            onChange={onChange}
            onError={onError}
            error={error}
            required
            dataTestId="lease_start"
            minDate={dayjs().subtract(1, 'year')}
            maxDate={dayjs().add(1, 'year')}
          />
        );
      }}
    />
  );
};
